import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Main Container for the book review
const BookReviewContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgb(var(--bg));
  min-height: 100vh;
  font-family: 'Arial', sans-serif;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 10% 0%, rgba(212,165,40,0.12), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

// Title styling
const Title = styled.h1`
  font-size: 2.5rem;
  color: #d4a528;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

// Author styling
const Author = styled.p`
  font-size: 1.2rem;
  color: rgb(var(--fg) / 0.78);
  margin-bottom: 1rem;
  font-style: italic;
`;

// Cover Image styling
const CoverImage = styled.img`
  width: 100%;
  max-width: 300px;
  max-height: 600px;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 20px 40px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
`;

// Date Published styling
const DatePublished = styled.p`
  font-size: 1rem;
  color: rgb(var(--gray) / 0.55);
  margin-bottom: 2rem;
`;

// Content Section styling
const ContentSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 2rem;
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
`;

// Section Title styling
const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #d4a528;
  border-bottom: 1px solid rgba(212, 165, 40, 0.3);
  padding-bottom: 0.5rem;
`;

// Section Content styling
const SectionContent = styled.div`
  font-size: 1rem;
  line-height: 1.8;
  color: rgb(var(--fg) / 0.82);
`;

// Quote styling
const Quote = styled.blockquote`
  font-style: italic;
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgb(var(--fg) / 0.04);
  border-left: 3px solid #d4a528;
  border-radius: 8px;
  color: rgb(var(--fg) / 0.78);
`;

// List Item styling
const ListItem = styled.li`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

// Ordered List styling
const OrderedList = styled.ol`
  padding-left: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

// Tags Container styling
const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: 800px;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

// Tag styling
const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  border: 1px solid rgba(212, 165, 40, 0.3);
  color: rgb(var(--fg) / 0.9);
  padding: 0.4rem 0.7rem;
  border-radius: 12px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
`;

const Button = styled.button`
  display: inline-block;
  margin: 2rem 0;
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.92);
  border: 1px solid rgb(var(--fg) / 0.14);
  padding: 0.9rem 2rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(212, 165, 40, 0.5);
  }
`;

export const GunsGermsSteelMetadata = {
  title: "Guns, Germs, and Steel",
  description: "A short history of everybody for the last 13,000 years.",
  tags: ['History', 'Geography', 'Anthropology', 'Civilization'],
  author: "Jared Diamond",
  datePublished: "2025-02-02",
  category: "Book Review",
  path: "/book-review/guns-germs-and-steel",
  coverImage: "/images/gun_germs_steel.jpg",
};

const GunsGermsSteel = () => {
  const navigate = useNavigate();
  const metadata = GunsGermsSteelMetadata;

  return (
    <BookReviewContainer>
      <Title>{metadata.title}</Title>
      <Author>by {metadata.author}</Author>
      <CoverImage src={metadata.coverImage} alt={metadata.title} />
      <DatePublished>Date Published: {metadata.datePublished}</DatePublished>
      <TagContainer>
        {metadata.tags.map((tag, index) => (
          <Tag key={index}>{tag}</Tag>
        ))}
      </TagContainer>
      <br />
      <br />

      <ContentSection>
        <SectionTitle>Summary</SectionTitle>
        <SectionContent>
          <p>
            "Guns, Germs, and Steel" explores the factors that shaped the modern world by examining why certain civilizations advanced faster than others. Jared Diamond argues that geography, not inherent differences among people, played the key role in shaping history.
          </p>
          <br />
          <p>
            The book investigates the influence of agriculture, domestication, disease, and technological advancements on the development of societies. Diamond debunks traditional narratives of cultural superiority and instead presents a compelling argument for environmental determinism.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Key Themes or Concepts</SectionTitle>
        <SectionContent>
          <OrderedList>
            <ListItem><strong>Geographic Determinism:</strong> The impact of geography and environment on societal development.</ListItem>
            <ListItem><strong>Agriculture and Domestication:</strong> How early access to food production shaped civilization.</ListItem>
            <ListItem><strong>Disease as a Weapon:</strong> How germs and immunity shaped global conflicts.</ListItem>
            <ListItem><strong>Technology and Innovation:</strong> The role of steel, writing, and tools in shaping power structures.</ListItem>
            <ListItem><strong>Collapse of Societies:</strong> How environmental and cultural factors led to the downfall of civilizations.</ListItem>
          </OrderedList>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Favorite Quotes</SectionTitle>
        <SectionContent>
          <Quote>
            "History followed different courses for different peoples because of differences among their environments, not because of biological differences among the peoples themselves."
          </Quote>
          <Quote>
            "The striking differences between the long-term histories of peoples of the different continents have been due not to innate differences in the peoples themselves but to differences in their environments."
          </Quote>
        </SectionContent>
      </ContentSection>

      {/* Personal Reflection Section */}
      <ContentSection>
        <SectionTitle>Personal Reflection</SectionTitle>
        <SectionContent>
          <p>
            I picked up this book after a recommendation from my Environmental Science course professor, expecting a deep dive into the role of geography in shaping civilizations. What I got was a well-structured argument on how access to resources, agriculture, and immunity shaped the balance of power throughout history. It was an interesting read that explained a lot of patterns I had come across before but never really thought about in this way.
          </p>
          <br />
          <p>
            While reading, I kept thinking back to <a href="/book-review/prisoners-of-geography">Prisoners of Geography</a>. That book looks at how geography still influences modern politics, and I found that it complemented Diamond’s ideas well. Where Diamond explains why some civilizations advanced earlier, Tim Marshall examines how those early advantages still play a role in today’s world. Seeing those connections helped me understand how much of history is shaped by things outside human control.
          </p>
          <br />
          <p>
            Another book that came to mind was <a href="/book-review/the-world-for-sale">The World for Sale</a>. Diamond explains how control over food, weapons, and technology helped certain civilizations expand, and that book takes it into the present by showing how global trade in commodities—oil, food, and metals—still drives economic and political power. It was interesting to see how the same themes apply whether we’re talking about ancient empires or modern corporations.
          </p>
          <br />
          <p>
            One of the more thought-provoking aspects was how early inequalities compounded over time, which made me think of <a href="/book-review/weapons-of-math-destruction">Weapons of Math Destruction</a>. Diamond focuses on historical advantages like agriculture and immunity, while that book looks at how modern systems—like biased algorithms—continue to create disparities. Both books highlight how initial advantages, whether in history or in data, tend to reinforce themselves over time.
          </p>
        </SectionContent>
        </ContentSection>
      <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
    </BookReviewContainer>
  );
};

export default GunsGermsSteel;
